import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, AlertCircle, X, MapPin, Wallet, Users, Building2, Timer, ChevronDown, ChevronUp, Heart, Send, CheckCircle2, AlertTriangle, Info, ArrowUpDown, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import RoleSaveButton from './RoleSaveButton'; // Adjust the path as needed
import { RejectFeedbackModal, ThankYouPopup, CalendlyPopup } from "../modals/FeedbackModals";
import AcceptInterviewModal from '../modals/AcceptInterviewModal'; // Import the modals
import RationaleModal from '../modals/RationaleModal';
import { createPortal } from 'react-dom';
import { trackEvent, trackEngagementAction } from "../../lib/analytics";

// Extract a usable video URL from Airtable-style field values.
// Accepts string URL, array of strings, attachment object(s) with { url } or thumbnails.
const extractVideoIntroUrl = (videoField) => {
  if (!videoField) return "";

  if (typeof videoField === "string") return videoField.trim();

  if (Array.isArray(videoField)) {
    for (const item of videoField) {
      const url = extractVideoIntroUrl(item);
      if (url) return url;
    }
    return "";
  }

  if (typeof videoField === "object") {
    if (typeof videoField.url === "string" && videoField.url.trim()) {
      return videoField.url.trim();
    }
    const thumbs = videoField.thumbnails;
    if (thumbs && typeof thumbs === "object") {
      const sizes = ["full", "large", "small"];
      for (const size of sizes) {
        const t = thumbs[size];
        if (t && typeof t.url === "string" && t.url.trim()) return t.url.trim();
      }
    }
  }

  return "";
};

// Status Comment Modal Component
function StatusCommentModal({ isOpen, onClose, onSubmit, buttonInfo }) {
  const [comment, setComment] = useState('');
  
  if (!isOpen || !buttonInfo) return null;

  const handleSubmit = () => {
    onSubmit(comment);
    setComment(''); // Reset comment after submission
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl"
      >
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">{buttonInfo.icon}</span>
          <h2 className="text-xl font-semibold text-gray-800">{buttonInfo.label}</h2>
        </div>
        
        <p className="text-gray-600 text-sm mb-4">
          {buttonInfo.description && `${buttonInfo.description}. `}
          Would you like to add any additional details? (optional)
        </p>
        
        <div className="space-y-2 mb-6">
          <label className="text-sm font-medium text-gray-700">Additional notes:</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="e.g. rejected at final stage interview, emailed to withdraw as not the right fit for me"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-[#02B491]/20 focus:border-[#02B491] transition-all"
            rows={3}
            maxLength={200}
          />
          <p className="text-xs text-gray-500">{comment.length}/200 characters</p>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded-lg transition-colors"
          >
            Update Status
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// Offer Confirmation Modal Component
function OfferConfirmationModal({ isOpen, onClose, onStatusSelect, companyName }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        className="bg-white w-full max-w-sm p-5 rounded-lg shadow-xl"
      >
        <div className="text-center mb-4">
          <span className="text-2xl">🎉</span>
          <h2 className="text-lg font-semibold text-gray-800 mt-2">Congrats on your offer from {companyName}!</h2>
        </div>
        
        <div className="space-y-2">
          <button
            onClick={() => onStatusSelect('offer-received')}
            className="w-full py-2.5 px-4 text-sm font-medium text-[#02B491] bg-white border border-[#02B491] rounded hover:bg-green-50 transition-colors"
          >
            💙 I'm excited, but still considering
          </button>
          <button
            onClick={() => onStatusSelect('offer-accepted')}
            className="w-full py-2.5 px-4 text-sm font-medium text-[#02B491] bg-white border border-[#02B491] rounded hover:bg-green-50 transition-colors"
          >
            ✅ I've accepted the offer!
          </button>
          <button
            onClick={() => onStatusSelect('offer-rejected')}
            className="w-full py-2.5 px-4 text-sm font-medium text-[#02B491] bg-white border border-[#02B491] rounded hover:bg-green-50 transition-colors"
          >
            🚫 I've decided to turn it down
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 text-sm font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded transition-colors"
          >
            Oops — I'm actually still interviewing!
          </button>
        </div>
      </motion.div>
    </div>
  );
}

const timeAgo = (dateString) => {
  const lastModifiedDate = new Date(dateString);
  const now = new Date();
  const differenceInSeconds = Math.floor((now - lastModifiedDate) / 1000);
  
  const twoDaysInSeconds = 2 * 24 * 60 * 60;
  
  if (differenceInSeconds > twoDaysInSeconds) {
      return null;
  }

  let value, unit;
  
  if (differenceInSeconds < 60) {
      return 'Added just now';
  }
  
  if (differenceInSeconds < 3600) {
    value = Math.floor(differenceInSeconds / 60);
    unit = 'minute';
  } else if (differenceInSeconds < 86400) {
    value = Math.floor(differenceInSeconds / 3600);
    unit = 'hour';
  } else {
    value = Math.floor(differenceInSeconds / 86400);
    unit = 'day';
  }
  
  return `Added ${value} ${unit}${value !== 1 ? 's' : ''} ago`;
};


// Apply Confirmation Modal Component
function ApplyConfirmModal({ isOpen, onClose, onConfirm, roleTitle, Company }) {
  const [agreementChecked, setAgreementChecked] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        className="bg-white w-full max-w-lg p-6 rounded-xl shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">🚀</span>
          <h2 className="text-xl font-semibold text-gray-800">Confirm Your Application</h2>
        </div>
        <p className="text-gray-600 text-sm mb-4">
          You are applying for the role: <strong className="text-gray-700">{roleTitle} at {Company}</strong>.
        </p>
        
        {/* Interview Rules Checklist */}
                 <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
           <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center">
              <span className="mr-2">📋</span>
              Interview Guidelines
            </h3>
           <div className="space-y-3 text-sm text-gray-700">
             <div className="flex items-start space-x-3">
               <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
               <span className="font-medium">Email the startup within 24 hrs of being invited to interview with your availability</span>
             </div>
             <div className="flex items-start space-x-3">
               <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
               <span className="font-medium">Test your tech (e.g. Zoom/Google Meet) in advance</span>
             </div>
             <div className="flex items-start space-x-3">
               <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
               <span className="font-medium">Join the interview at least 5 mins early</span>
             </div>
             <div className="flex items-start space-x-3">
               <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
               <span className="font-medium">Send a thank you email straight after the interview</span>
             </div>
             <div className="flex items-start space-x-3">
               <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
               <span className="font-medium">Complete any case study task before the deadline</span>
             </div>
             <div className="flex items-start space-x-3">
               <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
               <span className="font-medium">Only cancel if absolutely necessary, and with 24+ hrs notice</span>
             </div>
           </div>
         </div>

        {/* Agreement Checkbox */}
        <div className="flex items-start space-x-3 mb-6">
          <input
            type="checkbox"
            id="interview-agreement"
            checked={agreementChecked}
            onChange={(e) => setAgreementChecked(e.target.checked)}
            className="mt-1 h-4 w-4 text-[#02B491] focus:ring-[#02B491] border-gray-300 rounded"
          />
          <label htmlFor="interview-agreement" className="text-sm text-gray-700 cursor-pointer">
            I agree to follow the interview guidelines above and the startup's hiring process if selected. 
            I confirm I'm genuinely interested and ready to engage.
          </label>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!agreementChecked}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              agreementChecked 
                ? 'bg-[#02B491] hover:bg-[#018268] cursor-pointer' 
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Confirm & Apply
          </button>
        </div>
      </motion.div>
    </div>
  );
}



function RoleCard({ role, currentlyPlaying, setCurrentlyPlaying, viewMode, onSaveToggle, shortlistedRoles = [], shortlistedRolesLoaded = false, showSaveButton = true, isRecommended = false, matchScore, matchQuality, compactView = false, onApplyNow, appliedRoles = [], appliedRolesLoaded = false, showApplyNowButton = true, isMatchRequest = false, onInterviewActionComplete, showUpdateStatusButton = false, onUpdateStatus, interviewingRoles = [], interviewingRolesLoaded = false, unsuccessfulRoles = [], unsuccessfulRolesLoaded = false, applicationPendingRoles = [], applicationPendingRolesLoaded = false, currentFilterStage, debug = false, candidateData = null, onBookCall = null, onRequireVideo = null }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [showModal, setShowModal] = useState(false);

  // Group spotlight details to allow separate tabs if necessary
  const hasSpotlights = role.spotlightDetails && role.spotlightDetails.length > 0;

  // Check if there are different types of spotlights
  const companyOnlySpotlights = hasSpotlights ? role.spotlightDetails.filter(
    (spot) => spot["Type"] === "Company information"
  ) : [];

  const roleOnlySpotlights = hasSpotlights ? role.spotlightDetails.filter(
    (spot) => spot["Type"] === "Role information"
  ) : [];

  const companyAndRoleSpotlights = hasSpotlights ? role.spotlightDetails.filter(
    (spot) => spot["Type"] === "Company and Role"
  ) : [];

  // Determine which tabs to show based on spotlight types
  const hasOnlyCompanyAndRole = companyAndRoleSpotlights.length > 0 && 
    companyOnlySpotlights.length === 0 && 
    roleOnlySpotlights.length === 0;

  // Filter spotlights for company and role tabs
  const companySpotlights = hasSpotlights && !hasOnlyCompanyAndRole
    ? role.spotlightDetails.filter(
        (spot) => spot["Type"] === "Company information" || spot["Type"] === "Company and Role"
      )
    : [];

  const roleSpotlights = hasSpotlights && !hasOnlyCompanyAndRole
    ? role.spotlightDetails.filter(
        (spot) => spot["Type"] === "Role information" || spot["Type"] === "Company and Role"
      )
    : [];

  const initialTab = companySpotlights.length > 0 ? "company" : (roleSpotlights.length > 0 ? "role" : "company");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showFullKeySkills, setShowFullKeySkills] = useState(false);
  const [showFullInterview, setShowFullInterview] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const descriptionRef = useRef(null);
  const [isDescriptionLong, setIsDescriptionLong] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const imageRef = useRef(null);
  // Track which Loom videos have been explicitly clicked to enable autoplay and avoid duplicate events
  const loomPlayedSetRef = useRef(new Set());
  const [loomOverlayNonce, setLoomOverlayNonce] = useState(0);
  const confirmedVideoUrlRef = useRef("");

  // State for 'Apply Now' button
  const [isApplying, setIsApplying] = useState(false);
  const [applyStatus, setApplyStatus] = useState({ message: '', type: '' }); // type: 'success' or 'error'

  // State for the confirmation popup
  const [showApplyConfirmPopup, setShowApplyConfirmPopup] = useState(false);

  // State for the rationale modal
  const [showRationaleModal, setShowRationaleModal] = useState(false);

  // Add state for match request actions
  const [accepted, setAccepted] = useState(false);
  const [active, setActive] = useState(true);
  const [error, setError] = useState("");
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [rejectionFeedback, setRejectionFeedback] = useState("");
  const [rejectionCompleted, setRejectionCompleted] = useState(false);

  // State for loading on accept/decline
  const [isAccepting, setIsAccepting] = useState(false);
  
  // State for AcceptInterviewModal
  const [showAcceptInterviewModal, setShowAcceptInterviewModal] = useState(false);
  const [interviewAccepted, setInterviewAccepted] = useState(false);

  // State for inline status updates
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);
  
  // State for sub-option flow
  const [showSubOptions, setShowSubOptions] = useState(false);
  const [statusComment, setStatusComment] = useState('');
  
  // State for undo functionality
  const [isUndoing, setIsUndoing] = useState(false);
  
  // State for status comment modal
  const [showStatusCommentModal, setShowStatusCommentModal] = useState(false);
  const [selectedSubOption, setSelectedSubOption] = useState(null);

  // State for offer confirmation modal
  const [showOfferConfirmModal, setShowOfferConfirmModal] = useState(false);

  // State for dropdown
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // State for "still interviewing" sub-options
  const [showStillInterviewingOptions, setShowStillInterviewingOptions] = useState(false);

  // Ref for dropdown to handle click outside
  const dropdownRef = useRef(null);

  const lastModifiedString = role["Role Drop Status (Last modified)"];
  const addedAgo = lastModifiedString ? timeAgo(lastModifiedString) : null;
  let isRecent = false;
  if (lastModifiedString) {
    const lastModifiedDate = new Date(lastModifiedString);
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
    if (lastModifiedDate > twoDaysAgo) {
      isRecent = true;
    }
  }

  // Handle click outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if the candidate has already applied to this role (including all applications)
  const hasAlreadyApplied = appliedRolesLoaded && applicationPendingRolesLoaded && (appliedRoles.includes(role.id) || applicationPendingRoles.includes(role.id));
  
  // Check if all required data is loaded to prevent race conditions
  const isDataFullyLoaded = appliedRolesLoaded && applicationPendingRolesLoaded && interviewingRolesLoaded && unsuccessfulRolesLoaded;
  
  // Debug logging for application pending status
  console.log("=== Debug hasAlreadyApplied ===", {
    roleId: role.id,
    roleTitle: role["Role Title"],
    appliedRoles: appliedRoles,
    applicationPendingRoles: applicationPendingRoles,
    appliedRolesLoaded: appliedRolesLoaded,
    applicationPendingRolesLoaded: applicationPendingRolesLoaded,
    inAppliedRoles: appliedRoles.includes(role.id),
    inApplicationPendingRoles: applicationPendingRoles.includes(role.id),
    hasAlreadyApplied: hasAlreadyApplied
  });

  // Check if this application was unsuccessful
  const isUnsuccessful = unsuccessfulRolesLoaded && unsuccessfulRoles.includes(role.id);

  // Check if the candidate is currently interviewing for this role
  const isCurrentlyInterviewing = interviewingRolesLoaded && interviewingRoles.includes(role.id);

  // Set the initial saved state based on props instead of API call
  useEffect(() => {
    if (shortlistedRolesLoaded) {
      setIsSaved(shortlistedRoles.includes(role.id));
    }
  }, [role.id, shortlistedRoles, shortlistedRolesLoaded]);

  // Set initial apply status if already applied
  useEffect(() => {
    if (hasAlreadyApplied) {
      setApplyStatus({ message: '', type: 'success' });
    }
  }, [hasAlreadyApplied]);

  const handleSaveToggle = async () => {
    if (isUpdating) return; // Prevent multiple clicks while updating
    
    try {
      setIsUpdating(true);
      setSaveError(null);
      
      // Update UI immediately for a responsive feel
      const newSavedState = !isSaved;
      setIsSaved(newSavedState);
      
      // Notify parent component about the save action right away
      if (onSaveToggle) {
        onSaveToggle(newSavedState, role["Role Title"], role.id);
      }
      // Track intent immediately
      trackEvent("Role Save Clicked", {
        pathname: window.location.pathname,
        role_id: role.id,
        role_title: role["Role Title"],
        action: newSavedState ? "save" : "unsave",
      });
      
      const token = localStorage.getItem('token');

      // Make the API call in the background
      const response = await axios.post('/api/candidates/updateShortlistedRoles', 
        { 
          roleId: role.id,
          action: isSaved ? 'remove' : 'add'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('Save response:', response.data);
      // Track success
      trackEvent("Role Save Updated", {
        role_id: role.id,
        role_title: role["Role Title"],
        saved: newSavedState,
      });
      
      // If the API call fails, we can revert the UI state
      // This is handled in the catch block
    } catch (error) {
      console.error('Error updating shortlisted roles:', error);
      
      // Revert the UI state if the API call fails
      setIsSaved(isSaved);
      
      // Show error message
      setSaveError(error.response?.data?.message || 'Failed to update. Please try again.');
      // Error will automatically clear after 3 seconds
      setTimeout(() => setSaveError(null), 3000);
      
      // Also notify parent to revert the notification if needed
      if (onSaveToggle) {
        onSaveToggle(isSaved, role["Role Title"], role.id);
      }
      // Track error
      trackEvent("Role Save Error", {
        role_id: role.id,
        role_title: role["Role Title"],
        error: error.response?.data?.message || error.message || 'unknown',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInternalApplyNow = async () => {
    // If already applied, currently interviewing, unsuccessful, or in loading state, do nothing
    if (isApplying || !onApplyNow || hasAlreadyApplied || isCurrentlyInterviewing || isUnsuccessful) return;
    
    // Pre-apply guard: delegate to parent modal based on stream without video
    const streamStr = (candidateData?.stream || '').toLowerCase();
    const isSales = streamStr.includes('sales');
    const isMarketing = streamStr.includes('marketing');
    const isGeneralist = streamStr.includes('generalist');
    const candidateVideoFromProps = extractVideoIntroUrl(candidateData?.video);
    const candidateVideoFromRef = extractVideoIntroUrl(confirmedVideoUrlRef.current);
    let hasVideo = !!(candidateVideoFromRef || candidateVideoFromProps);
    
    // Avoid false-negative "no video" popups for Sales caused by stale cache / race:
    // If video appears missing, do a quick fresh candidate fetch before triggering the modal.
    if ((isSales || isMarketing || isGeneralist) && !hasVideo) {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const resp = await axios.get("/api/candidates/fetchCandidate", {
            headers: { Authorization: `Bearer ${token}` },
          });
          const fields = resp?.data?.candidate?.fields || {};
          const freshVideoField =
            fields["Video intro (new)"] ||
            fields["Raw video intro"] ||
            "";
          const freshVideoUrl = extractVideoIntroUrl(freshVideoField);
          if (freshVideoUrl) {
            confirmedVideoUrlRef.current = freshVideoUrl;
            hasVideo = true;
          }
        }
      } catch (_) {}

      if (!hasVideo) {
        if (typeof onRequireVideo === 'function') {
          onRequireVideo(role.id, role["Role Title"], null);
        }
        return;
      }
    }

    // Check if rationale is required for this role
    if (role["Rationale required?"]) {
      setShowRationaleModal(true);
      try {
        trackEvent("Quick Apply To Rationale Role", { role_id: role.id, role_title: role["Role Title"] });
      } catch (_) {}
    } else {
      setShowApplyConfirmPopup(true);
    }
    // Standalone event for Quick Apply click
    try {
      trackEvent("Quick Apply Clicked", { role_id: role.id, role_title: role["Role Title"] });
    } catch (_) {}
    // Buffer into engagement stream as well
    trackEngagementAction("cta_click", { cta: "quick_apply", role_id: role.id, role_title: role["Role Title"] });
  };

  const handleRationaleSubmit = async (rationale) => {
    setShowRationaleModal(false);
    setIsApplying(true);
    setApplyStatus({ message: '', type: '' });
    // Track dedicated rationale submission event
    try {
      trackEvent("Rationale Submitted", { role_id: role.id, role_title: role["Role Title"] });
    } catch (_) {}

    try {
      const success = await onApplyNow(role.id, role["Role Title"], rationale);
      if (success) {
        // Parent handler will emit "Application Submitted" once on success
        setApplyStatus({ message: 'Applied successfully!', type: 'success' });
      } else {
        setApplyStatus({ message: 'Application failed. Already applied or error occurred.', type: 'error' });
      }
    } catch (error) {
      console.error('Error in onApplyNow call:', error);
      setApplyStatus({ message: error.message || 'An unexpected error occurred.', type: 'error' });
    } finally {
      setIsApplying(false);
      setTimeout(() => setApplyStatus({ message: '', type: '' }), 4000);
    }
  };

  const executeApplyNow = async () => {
    setShowApplyConfirmPopup(false); // Close modal
    setIsApplying(true);
    setApplyStatus({ message: '', type: '' });

    try {
      const success = await onApplyNow(role.id, role["Role Title"]);
      if (success) {
        // Parent handler will emit "Application Submitted" once on success
        setApplyStatus({ message: 'Applied successfully!', type: 'success' });
      } else {
        // The onApplyNow function itself should set specific error messages if it returns false
        setApplyStatus({ message: 'Application failed. Already applied or error occurred.', type: 'error' });
      }
    } catch (error) {
      console.error('Error in onApplyNow call:', error);
      setApplyStatus({ message: error.message || 'An unexpected error occurred.', type: 'error' });
    } finally {
      setIsApplying(false);
      setTimeout(() => setApplyStatus({ message: '', type: '' }), 4000); // Clear status after 4 seconds
    }
  };

  const handlePlay = () => {
    if (currentlyPlaying && currentlyPlaying !== videoRef.current) {
      currentlyPlaying.pause();
    }
    setCurrentlyPlaying(videoRef.current);
    
    // Standalone event for spotlight watching - only fires on user click
    trackEvent("Spotlight Watched", {
      role_id: role.id,
      role_title: role["Role Title"],
      video_source: "dropbox",
      page: "roles_list"
    });
  };

  // Add debugging when component mounts
  useEffect(() => {
    if (!role) {
      console.log("RoleCard mounted but role prop is missing!");
      return;
    }
    console.log("RoleCard mounted for role:", role["Role Title"]);
    
    // Debug log for role data - GUARDED
    console.log("Role data:", {
      id: role.id,
      title: role["Role Title"],
      company: role["Company"],
      // Check if role is an object before getting keys
      keys: typeof role === 'object' && role !== null ? Object.keys(role) : 'Role is not an object or is null' 
    });
    
    // Debug log for image field - GUARDED
    const coverImageField = role["Role Cover Image (from Startup)"];
    console.log("Role image data:", {
      roleTitle: role["Role Title"],
      coverImage: coverImageField,
      type: typeof coverImageField,
      isArray: Array.isArray(coverImageField)
    });
    
    // Check before using Object.keys on potentially null/undefined object
    if (Array.isArray(coverImageField)) {
      console.log("Image array first item:", coverImageField[0]);
    } else if (typeof coverImageField === 'object' && coverImageField !== null) {
      console.log("Image object keys:", Object.keys(coverImageField));
    }
  }, [role]);

  const fallbackImageUrl =
    "https://placehold.co/400x300/e2e8f0/64748b?text=No+Image";

  // Extract the cover image URL properly from Airtable data structure
  const getCoverImageUrl = () => {
    // GUARD: Check if role exists first
    if (!role) return fallbackImageUrl; 

    const imageField = role["Role Cover Image (from Startup)"];

    if (!imageField) {
      return fallbackImageUrl;
    }
    
    // Case 1: It's a string URL
    if (typeof imageField === 'string') {
      return imageField;
    }
    
    // Case 2: It's an array of attachment objects
    if (Array.isArray(imageField)) {
      const images = imageField;
      if (images.length > 0) {
        // First image object
        const firstImage = images[0];
        
        // GUARD: Check if firstImage is a valid object before accessing properties
        if (firstImage && typeof firstImage === 'object') {
          // Case 2a: The array item has a url property
          if (firstImage.url) {
            return firstImage.url;
          }
          
          // Case 2c: The array item has a thumbnails property (common in Airtable)
          if (firstImage.thumbnails) {
            const sizes = ['large', 'full', 'small'];
            for (const size of sizes) {
              // GUARD: Check nested properties carefully
              if (firstImage.thumbnails[size] && firstImage.thumbnails[size].url) {
                return firstImage.thumbnails[size].url;
              }
            }
          }
        }
        
        // Case 2b: The array item itself is a string URL
        if (typeof firstImage === 'string') {
          return firstImage;
        }
      }
    }
    
    // Case 3: It's an object
    // GUARD: Check it's not null before treating as an object
    if (typeof imageField === 'object' && imageField !== null) {
      const imageObj = imageField;
      
      // Case 3a: Direct URL property
      if (imageObj.url) {
        return imageObj.url;
      }
      
      // Case 3b: Thumbnails property
      if (imageObj.thumbnails) {
        const sizes = ['large', 'full', 'small'];
        for (const size of sizes) {
           // GUARD: Check nested properties carefully
          if (imageObj.thumbnails[size] && imageObj.thumbnails[size].url) {
            return imageObj.thumbnails[size].url;
          }
        }
      }
    }
    
    console.log("Unable to extract image URL, using fallback for:", role["Role Title"]);
    return fallbackImageUrl;
  };

  // Get the image URL using our helper function
  const coverImageUrl = getCoverImageUrl();

  // Add this effect to check if description is longer than 2 lines
  useEffect(() => {
    if (descriptionRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(descriptionRef.current).lineHeight);
      const height = descriptionRef.current.scrollHeight;
      setIsDescriptionLong(height > lineHeight * 2);
    }
  }, [role]);

  // Helper to render a single spotlight video (Dropbox or Loom)
  const renderSpotlightVideo = (spot, index) => {
    const dropboxUrl =
      spot["URL Dropbox cleaned"] &&
      Array.isArray(spot["URL Dropbox cleaned"]) &&
      spot["URL Dropbox cleaned"].length > 0
        ? spot["URL Dropbox cleaned"][0]
        : spot["URL Dropbox cleaned"];

    const loomUrl =
      spot["URL Loom cleaned"] &&
      Array.isArray(spot["URL Loom cleaned"]) &&
      spot["URL Loom cleaned"].length > 0
        ? spot["URL Loom cleaned"][0]
        : spot["URL Loom cleaned"];

    if (dropboxUrl) {
      return (
        <video
          key={index}
          poster={coverImageUrl}
          src={dropboxUrl}
          className={`w-full border object-cover rounded-xl bg-[var(--overlay-2)] mb-4 ${compactView ? 'h-40' : 'h-72'}`}
          controls
          onPlay={handlePlay}
          onPlaying={() => {
            // Only track for page engagement, not standalone
            trackEngagementAction("video_play", { role_id: role.id, role_title: role["Role Title"], source: "dropbox" });
          }}
          onTimeUpdate={(e) => {
            try {
              const v = e.currentTarget;
              if (!v || !v.duration || v.duration === Infinity) return;
              const pct = Math.round((v.currentTime / v.duration) * 100);
              const milestones = [25, 50, 75, 100];
              // store per element to avoid spamming
              v.__mpMilestones = v.__mpMilestones || new Set();
              milestones.forEach(m => {
                if (pct >= m && !v.__mpMilestones.has(m)) {
                  v.__mpMilestones.add(m);
                  trackEngagementAction("video_progress", { role_id: role.id, role_title: role["Role Title"], percent: m, source: "dropbox" });
                }
              });
            } catch (_) {}
          }}
          onPosterError={() => console.log("Video poster failed to load")}
          ref={videoRef}
        />
      );
    } else if (loomUrl) {
      // Ensure we only trigger on explicit user click and then allow the iframe to receive interactions
      const loomKey = `${index}-${loomUrl}`;
      const hasPlayed = loomPlayedSetRef.current.has(loomKey);
      const effectiveLoomSrc = hasPlayed ? (loomUrl.includes('?') ? `${loomUrl}&autoplay=1` : `${loomUrl}?autoplay=1`) : loomUrl;
      return (
        <div className={`w-full ${compactView ? 'h-40' : 'h-72'} rounded-xl mb-4 overflow-hidden relative`}>
          <iframe
            key={index}
            src={effectiveLoomSrc}
            className="w-full h-full object-cover rounded-xl bg-[var(--overlay-2)]"
            title={`Spotlight Video ${index}`}
            onError={(e) => {
              console.error("Failed to load loom iframe at", loomUrl);
              // If iframe fails to load, show a message
              const errorMessage = document.createElement('div');
              errorMessage.className = "w-full h-full flex items-center justify-center bg-gray-100 text-gray-500 text-center p-4";
              errorMessage.innerHTML = `
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto h-10 w-10 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                  </svg>
                  <p>Video content unavailable</p>
                  <p class="text-sm mt-1">Please check the role details page for more information</p>
                </div>
              `;
              if (e.target.parentElement) {
                e.target.parentElement.replaceChild(errorMessage, e.target);
              }
            }}
          />
          {/* Click-capturing overlay above iframe - removed after first click */}
          {!hasPlayed && (
            <div
              className="absolute inset-0 bg-transparent cursor-pointer z-10"
              onClick={() => {
                try {
                  trackEvent("Spotlight Watched", {
                    role_id: role.id,
                    role_title: role["Role Title"],
                    video_source: "loom",
                    page: "roles_list"
                  });
                } catch (_) {}
                try {
                  trackEngagementAction("video_play", { role_id: role.id, role_title: role["Role Title"], source: "loom" });
                } catch (_) {}
                loomPlayedSetRef.current.add(loomKey);
                setLoomOverlayNonce((n) => n + 1);
              }}
              title="Click to play"
            />
          )}
          <div className="absolute inset-0 pointer-events-none border border-gray-200 rounded-xl"></div>
        </div>
      );
    } else {
      return null;
    }
  };

  // Inline stage badge to render next to the role title
  const renderStageBadge = () => {
    if (!role.stage) return null;
    // Hide the "Interviewing" badge when viewing the Interviewing tab
    if (role.stage === "Interviewing") {
      // Don't show the badge if we're currently on the Interviewing tab
      if (currentFilterStage === "Interviewing") {
        return null;
      }
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-green-50 text-green-700 border border-green-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1" />
          Interviewing
        </span>
      );
    }
    if (role.stage === "Offer made") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-blue-50 text-blue-700 border border-blue-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1" />
          Offer made
        </span>
      );
    }
    if (role.stage === "Match requested") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-purple-50 text-purple-700 border border-purple-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mr-1" />
          Match requested
        </span>
      );
    }
    if (role.stage === "Application pending") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-yellow-50 text-yellow-700 border border-yellow-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 mr-1" />
          Application pending
        </span>
      );
    }
    if (role.stage === "Rejected @ CV") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-red-50 text-red-700 border border-red-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1" />
          Rejected @ CV
        </span>
      );
    }
    if (role.stage === "Rejected @ Interview") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-red-50 text-red-700 border border-red-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1" />
          Rejected @ Interview
        </span>
      );
    }
    if (role.stage === "Rejected Interview Request") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-red-50 text-red-700 border border-red-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1" />
          Rejected Interview Request
        </span>
      );
    }
    if (role.stage === "Offer rejected") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-red-50 text-red-700 border border-red-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1" />
          Offer rejected
        </span>
      );
    }
    if (role.stage === "Offer retracted") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-red-50 text-red-700 border border-red-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1" />
          Offer retracted
        </span>
      );
    }
    if (role.stage === "Startup withdrawn") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-red-50 text-red-700 border border-red-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1" />
          Startup withdrawn
        </span>
      );
    }
    if (role.stage === "Candidate withdrawn") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-gray-50 text-gray-700 border border-gray-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mr-1" />
          Candidate withdrawn
        </span>
      );
    }
    if (role.stage === "Unsuccessful") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-red-50 text-red-700 border border-red-100 ml-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-1" />
          Unsuccessful
        </span>
      );
    }
    return null;
  };

  useEffect(() => {
    // Clear error after 3 seconds
    if (saveError) {
      const timer = setTimeout(() => setSaveError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveError]);

  // Function to handle inline status updates (replaces modal functionality)
  const handleInlineStatusUpdate = async (statusId, customText = '') => {
    if (isUpdatingStatus) return;
    
    // If "offer-received" is clicked, show confirmation modal instead of updating immediately
    if (statusId === 'offer-received' && !showOfferConfirmModal) {
      setShowOfferConfirmModal(true);
      return;
    }
    
    // If "no-longer-interviewing" is clicked, show sub-options instead of updating
    if (statusId === 'no-longer-interviewing') {
      setShowSubOptions(true);
      setShowStillInterviewingOptions(false); // Hide "still interviewing" options if open
      setShowStatusDropdown(false); // Close dropdown
      return;
    }

    // If "still-interviewing" is clicked, show sub-options instead of updating
    if (statusId === 'still-interviewing') {
      setShowStillInterviewingOptions(true);
      setShowSubOptions(false); // Hide "no longer interviewing" options if open
      setShowStatusDropdown(false); // Close dropdown
      return;
    }

    // Handle intermediate interview stages
    if (statusId === 'first-interview' || statusId === 'multiple-interview-rounds' || statusId === 'final-interview') {
      const intermediateStatusMap = {
        'first-interview': 'First interview',
        'multiple-interview-rounds': 'Passed the first round',
        'final-interview': 'Final interview'
      };
      
      setIsUpdatingStatus(true);
      setStatusUpdateError(null);
      setSelectedStatus(statusId);
      
      try {
        const token = localStorage.getItem('token');
        
        const updateData = {
          fields: {
            'Candidate Interview Stage': intermediateStatusMap[statusId],
            'Updated on popup': false
          },
          interviewId: role.id
        };

        await axios.patch(`/api/interviews/updateInterview`, updateData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        // Show success state
        setStatusUpdateSuccess(true);
        setShowStillInterviewingOptions(false);
        
        // Refresh data after successful update
        if (onInterviewActionComplete) {
          onInterviewActionComplete();
        }
        
        console.log('Status updated successfully!');
        
      } catch (error) {
        console.error('Error updating interview status:', error);
        setStatusUpdateError('Failed to update status. Please try again.');
        setSelectedStatus(null);
        setTimeout(() => setStatusUpdateError(null), 3000);
      } finally {
        setIsUpdatingStatus(false);
      }
      return;
    }

    // For sub-options, show the comment modal instead of updating immediately
    if (statusId === 'rejected-at-interview' || statusId === 'candidate-withdrawn') {
      const subOptionButtons = [
        {
          id: 'rejected-at-interview',
          label: 'Rejected at interview',
          icon: '📝',
          description: 'Did not progress',
          backendValue: 'Rejected at interview'
        },
        {
          id: 'candidate-withdrawn',
          label: 'Withdrawn from process',
          icon: '🚪',
          description: 'You withdrew',
          backendValue: 'Candidate withdrawn'
        }
      ];
      
      const selectedButton = subOptionButtons.find(btn => btn.id === statusId);
      setSelectedSubOption(selectedButton);
      setShowStatusCommentModal(true);
      return;
    }
    
    setIsUpdatingStatus(true);
    setStatusUpdateError(null);
    setSelectedStatus(statusId);
    
    try {
      const token = localStorage.getItem('token');
      
      const statusMap = {
        'offer-received': 'Received an offer (pending decision)',
        'offer-accepted': 'Accepted offer',
        'offer-rejected': 'Offer rejected',
        'rejected-at-interview': 'Rejected at interview',
        'candidate-withdrawn': 'Candidate withdrawn'
      };
      
      const updateData = {
        fields: {},
        interviewId: role.id
      };

      if (statusMap[statusId]) {
        updateData.fields['Candidate Interview Stage'] = statusMap[statusId];
      }

      // Mark that this update did NOT come from the stale interview popup
      updateData.fields['Updated on popup'] = false;

      // If there's a comment, add it to the update
      if (customText.trim()) {
        updateData.fields['Candidate Interview Stage (other)'] = customText.trim();
      }

      await axios.patch(`/api/interviews/updateInterview`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Show success state
      setStatusUpdateSuccess(true);
      setShowSubOptions(false); // Reset sub-options view
      
      // Refresh data after successful update
      if (onInterviewActionComplete) {
        onInterviewActionComplete();
      }
      
      // Don't call onInterviewActionComplete immediately to avoid page refresh
      // The user can manually refresh or navigate away to see updated data
      console.log('Status updated successfully! The changes will be reflected when you refresh the page.');
      
    } catch (error) {
      console.error('Error updating interview status:', error);
      setStatusUpdateError('Failed to update status. Please try again.');
      setSelectedStatus(null);
      setTimeout(() => setStatusUpdateError(null), 3000);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleOfferDecision = (decision) => {
    setShowOfferConfirmModal(false);
    handleInlineStatusUpdate(decision);
  };

  // Function to handle undo - revert status back to "Interviewing"
  const handleUndoStatusUpdate = async () => {
    if (isUndoing) return;
    
    setIsUndoing(true);
    setStatusUpdateError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      const updateData = {
        fields: {
          'Candidate Interview Stage': 'Interviewing',
          'Updated on popup': false // Mark that this update did NOT come from the stale interview popup
        },
        interviewId: role.id
      };

      await axios.patch(`/api/interviews/updateInterview`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Reset success state and show interviewing status again
      setStatusUpdateSuccess(false);
      setSelectedStatus(null);
      setStatusComment('');
      setShowSubOptions(false);
      setShowStillInterviewingOptions(false);
      
      console.log('Status reverted to Interviewing successfully!');
      
    } catch (error) {
      console.error('Error reverting interview status:', error);
      setStatusUpdateError('Failed to undo status change. Please try again.');
      setTimeout(() => setStatusUpdateError(null), 3000);
    } finally {
      setIsUndoing(false);
    }
  };

  // Function to handle status comment modal submission
  const handleStatusCommentSubmit = async (comment) => {
    if (!selectedSubOption) return;
    
    setShowStatusCommentModal(false);
    setIsUpdatingStatus(true);
    setStatusUpdateError(null);
    setSelectedStatus(selectedSubOption.id);
    
    try {
      const token = localStorage.getItem('token');
      
      const updateData = {
        fields: {
          'Candidate Interview Stage': selectedSubOption.backendValue,
          'Updated on popup': false // Mark that this update did NOT come from the stale interview popup
        },
        interviewId: role.id
      };

      // If there's a comment, add it to the update
      if (comment.trim()) {
        updateData.fields['Candidate Interview Stage (other)'] = comment.trim();
        setStatusComment(comment); // Store for display in success message
      }

      await axios.patch(`/api/interviews/updateInterview`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Show success state
      setStatusUpdateSuccess(true);
      setShowSubOptions(false); // Reset sub-options view
      
      console.log('Status updated successfully! The changes will be reflected when you refresh the page.');
      
    } catch (error) {
      console.error('Error updating interview status:', error);
      setStatusUpdateError('Failed to update status. Please try again.');
      setSelectedStatus(null);
      setTimeout(() => setStatusUpdateError(null), 3000);
    } finally {
      setIsUpdatingStatus(false);
      setSelectedSubOption(null);
    }
  };

  // Function to handle status comment modal closure
  const handleStatusCommentModalClose = () => {
    setShowStatusCommentModal(false);
    setSelectedSubOption(null);
  };

  // Function to get border color based on current stage
  const getCardBorderClass = () => {
    // Always return neutral gray border regardless of stage
    return "border-gray-100";
  };

  // Status selection buttons for interviewing stage
  const renderInterviewStatusButtons = () => {
    if (role.stage !== "Interviewing" || compactView) return null;
    
    // Check if there's a previously selected status from backend data
    const candidateInterviewStage = role['Candidate Interview Stage'];
    
    // Determine if user has made a selection
    let hasExistingSelection = false;
    let displayStatusText = '';
    
    if (candidateInterviewStage && candidateInterviewStage !== 'Interviewing') {
      hasExistingSelection = true;
      
      // Map backend values to display text
      if (candidateInterviewStage === 'Received an offer (pending decision)') {
        displayStatusText = 'Received offer';
      } else if (candidateInterviewStage === 'Accepted offer') {
        displayStatusText = 'Accepted offer';
      } else if (candidateInterviewStage === 'Offer rejected') {
        displayStatusText = 'Offer rejected';
      } else if (candidateInterviewStage === 'Rejected at interview') {
        displayStatusText = 'Rejected';
      } else if (candidateInterviewStage === 'Candidate withdrawn') {
        displayStatusText = 'Withdrawn';
      } else if (candidateInterviewStage === 'First interview') {
        displayStatusText = 'First interview';
      } else if (candidateInterviewStage === 'Passed the first round') {
        displayStatusText = 'Passed the first round';
      } else if (candidateInterviewStage === 'Final interview') {
        displayStatusText = 'Final interview';
      } else {
        displayStatusText = candidateInterviewStage;
      }
    }

    // If status update was successful, show minimal success message
    if (statusUpdateSuccess && selectedStatus) {
      const statusLabels = {
        'offer-received': 'Received offer',
        'offer-accepted': 'Accepted offer',
        'offer-rejected': 'Offer rejected',
        'rejected-at-interview': 'Rejected',
        'candidate-withdrawn': 'Withdrawn',
        'first-interview': 'First interview',
        'multiple-interview-rounds': 'Passed the first round',
        'final-interview': 'Final interview'
      };
      
      const displayLabel = statusLabels[selectedStatus] || selectedStatus;
      
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm">✅</span>
              <span className="text-sm font-medium text-green-700">Status: {displayLabel}</span>
            </div>
            <button
              onClick={handleUndoStatusUpdate}
              disabled={isUndoing}
              className="text-xs text-gray-500 hover:text-gray-700 underline transition-colors disabled:opacity-50"
            >
              {isUndoing ? 'Undoing...' : 'undo'}
            </button>
          </div>
        </div>
      );
    }

    // Show sub-options if "Still interviewing" was clicked
    if (showStillInterviewingOptions) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Where are you in the interview process?</span>
            <button
              onClick={() => setShowStillInterviewingOptions(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {statusUpdateError && (
            <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
              {statusUpdateError}
            </div>
          )}
          
          <div className="space-y-2">
            <button
              onClick={() => handleInlineStatusUpdate('first-interview')}
              disabled={isUpdatingStatus}
              className="w-full py-2 px-3 text-xs font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 text-left"
            >
              {isUpdatingStatus && selectedStatus === 'first-interview' ? 'Updating...' : 'First interview'}
            </button>
            <button
              onClick={() => handleInlineStatusUpdate('multiple-interview-rounds')}
              disabled={isUpdatingStatus}
              className="w-full py-2 px-3 text-xs font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 text-left"
            >
              {isUpdatingStatus && selectedStatus === 'multiple-interview-rounds' ? 'Updating...' : 'I passed the first round'}
            </button>
            <button
              onClick={() => handleInlineStatusUpdate('final-interview')}
              disabled={isUpdatingStatus}
              className="w-full py-2 px-3 text-xs font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 text-left"
            >
              {isUpdatingStatus && selectedStatus === 'final-interview' ? 'Updating...' : 'Final interview'}
            </button>
          </div>
        </div>
      );
    }

    // Show sub-options if "No longer interviewing" was clicked
    if (showSubOptions) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">What happened?</span>
            <button
              onClick={() => setShowSubOptions(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {statusUpdateError && (
            <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
              {statusUpdateError}
            </div>
          )}
          
          <div className="flex gap-2">
            <button
              onClick={() => handleInlineStatusUpdate('rejected-at-interview')}
              disabled={isUpdatingStatus}
              className="flex-1 py-2 px-3 text-xs font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isUpdatingStatus && selectedStatus === 'rejected-at-interview' ? '...' : 'Rejected'}
            </button>
            <button
              onClick={() => handleInlineStatusUpdate('candidate-withdrawn')}
              disabled={isUpdatingStatus}
              className="flex-1 py-2 px-3 text-xs font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isUpdatingStatus && selectedStatus === 'candidate-withdrawn' ? '...' : 'I withdrew'}
            </button>
          </div>
        </div>
      );
    }

    // Main status selection interface - compact and minimal
    return (
      <div className="border border-gray-200 rounded-lg p-3 mb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Update your interview status</span>
            <div className="relative group">
              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[9999] w-64">
                <h4 className="font-medium text-gray-800 mb-1 text-sm">Stay Organised</h4>
                <p className="text-gray-600 text-xs leading-relaxed">
                  Keep your interview statuses up to date to easily track your progress and help us find you better matches.
                </p>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-200"></div>
              </div>
            </div>
          </div>
          {hasExistingSelection && (
            <span className="text-xs text-gray-500">{displayStatusText}</span>
          )}
        </div>

        {statusUpdateError && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
            {statusUpdateError}
          </div>
        )}

        {/* Status dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            disabled={isUpdatingStatus}
            className="w-full flex items-center justify-between py-2 px-3 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
                    <span className="flex items-center gap-2">
          {(!hasExistingSelection || candidateInterviewStage === 'Interviewing') && (
            <>
              <span>📝</span>
              <span>I'm still interviewing</span>
            </>
          )}
          {candidateInterviewStage === 'First interview' && (
            <>
              <span>📝</span>
              <span>First interview</span>
            </>
          )}
          {candidateInterviewStage === 'Passed the first round' && (
            <>
              <span>📝</span>
              <span>Passed the first round</span>
            </>
          )}
          {candidateInterviewStage === 'Final interview' && (
            <>
              <span>📝</span>
              <span>Final interview</span>
            </>
          )}
          {candidateInterviewStage === 'Received an offer (pending decision)' && (
            <>
              <span>🎉</span>
              <span>Received offer</span>
            </>
          )}
          {candidateInterviewStage === 'Accepted offer' && (
            <>
              <span>✅</span>
              <span>Accepted offer</span>
            </>
          )}
          {candidateInterviewStage === 'Offer rejected' && (
            <>
              <span>🚫</span>
              <span>Offer rejected</span>
            </>
          )}
          {(candidateInterviewStage === 'Rejected at interview' || candidateInterviewStage === 'Candidate withdrawn') && (
            <>
              <span>❌</span>
              <span>No longer interviewing</span>
            </>
          )}
        </span>
            <ArrowUpDown className="w-4 h-4 text-gray-500" />
          </button>

          {/* Dropdown menu */}
          {showStatusDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              <div className="py-1">
                <button
                  onClick={() => {
                    handleInlineStatusUpdate('still-interviewing');
                    setShowStatusDropdown(false);
                  }}
                  disabled={isUpdatingStatus}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <span>📝</span>
                  <span>I'm still interviewing</span>
                  {(!hasExistingSelection || 
                    candidateInterviewStage === 'Interviewing' || 
                    candidateInterviewStage === 'First interview' || 
                    candidateInterviewStage === 'Passed the first round' || 
                    candidateInterviewStage === 'Final interview') && (
                    <span className="ml-auto w-2 h-2 bg-[#02B491] rounded-full"></span>
                  )}
                </button>
                
                <button
                  onClick={() => {
                    handleInlineStatusUpdate('offer-received');
                    setShowStatusDropdown(false);
                  }}
                  disabled={isUpdatingStatus}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <span>🎉</span>
                  <span>Received offer</span>
                  {(candidateInterviewStage === 'Received an offer (pending decision)' || 
                    candidateInterviewStage === 'Accepted offer' || 
                    candidateInterviewStage === 'Offer rejected') && (
                    <span className="ml-auto w-2 h-2 bg-[#02B491] rounded-full"></span>
                  )}
                </button>
                
                <button
                  onClick={() => {
                    handleInlineStatusUpdate('no-longer-interviewing');
                    setShowStatusDropdown(false);
                  }}
                  disabled={isUpdatingStatus}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <span>❌</span>
                  <span>No longer interviewing</span>
                  {(candidateInterviewStage === 'Rejected at interview' || candidateInterviewStage === 'Candidate withdrawn') && (
                    <span className="ml-auto w-2 h-2 bg-[#02B491] rounded-full"></span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Minimal helper text only when no selection made */}
        {!hasExistingSelection && (
          <p className="text-xs text-gray-500 mt-2 text-center">
            Waiting for feedback? Choose "I'm still interviewing"
          </p>
        )}
      </div>
    );
  };

  // Choose the wrapper class based on whether it's a recommended role or not
  const cardWrapperClass = isRecommended 
    ? `${viewMode === "grid" ? "flex flex-col" : "md:flex"} bg-white rounded-xl overflow-visible h-full transition-all duration-300 border ${getCardBorderClass()} relative shadow-lg hover:shadow-xl hover:-translate-y-1`
    : viewMode === "carousel"
    ? `bg-white rounded-xl shadow-2xl transition-all duration-300 overflow-visible border-2 ${getCardBorderClass()} flex flex-col h-full relative`
    : `bg-white rounded-xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-visible border ${getCardBorderClass()} flex flex-col h-full relative`;

  // Button classes based on state
  const getApplyButtonClasses = () => {
    if (!isDataFullyLoaded) {
      return 'bg-gray-400 text-white cursor-not-allowed';
    } else if (isCurrentlyInterviewing) {
      return 'bg-green-500 text-white cursor-not-allowed shadow-md';
    } else if (isUnsuccessful) {
      return 'bg-red-500 text-white cursor-not-allowed shadow-md';
    } else if (hasAlreadyApplied) {
      return 'bg-green-500 text-white cursor-not-allowed shadow-md';
    } else if (isApplying) {
      return 'bg-gray-400 text-white cursor-not-allowed';
    } else if (applyStatus.type === 'success') {
      return 'bg-green-500 text-white cursor-not-allowed shadow-md';
    } else if (applyStatus.type === 'error') {
      return 'bg-red-500 text-white shadow-md';
    } else {
      return 'bg-[#02B491] border-2 border-[#02B491] text-white hover:bg-[#018268] hover:shadow-lg hover:scale-105';
    }
  };

  // Accept Interview Request handler
  const handleSubmit = async (stage, feedbackData = null) => {
    // Extract the actual Airtable record ID
    const recordId = role.recordId || role.airtableId || role.id;
    
    console.log("Using interview record ID:", recordId);
    console.log("Stage:", stage);
    console.log("Feedback data:", feedbackData);
    
    // Prepare the data structure to match what the backend expects
    const updatedData = {
      fields: {
        Stage: stage,
      },
      interviewId: recordId,
    };

    // If the request is being rejected, also update the candidate-facing stage
    if (stage === "Rejected Interview Request") {
      updatedData.fields["Candidate Interview Stage"] = "Rejected Match Request";
    }

    // Handle feedback data structure (same as working modal)
    if (feedbackData) {
      updatedData.fields["MR Rejection Feedback"] = feedbackData.reason;
      
      if (feedbackData.reason === "Other" && feedbackData.otherText) {
        updatedData.fields["MR Rejection Feedback (other)"] = feedbackData.otherText;
      }
    }
    
    setError("");
    if (stage === "Interviewing") {
      setIsAccepting(true);
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication token missing. Please log in again.");
        return;
      }
      
      console.log("Sending request with data:", updatedData);
      
      const response = await axios.patch(
        `/api/interviews/updateInterviewRequest`,
        updatedData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000 // 10 second timeout
        }
      );
      
      console.log("Response from update:", response.data);
      
      if (response.data && response.data.success) {
        if (stage === "Interviewing") {
          setAccepted(true);
          setInterviewAccepted(true); // Show success step in modal
          
          // Only show Calendly/Thank you popups if AcceptInterviewModal is not open
          if (!showAcceptInterviewModal) {
            // Check if there's a Calendly link available - handle different data types
            const calendlyUrlField = role["HL Calendly Link (if applicable)"];
            let calendlyUrl = null;
            
            if (calendlyUrlField) {
              if (typeof calendlyUrlField === 'string') {
                calendlyUrl = calendlyUrlField;
              } else if (Array.isArray(calendlyUrlField) && calendlyUrlField.length > 0) {
                calendlyUrl = calendlyUrlField[0];
              }
            }
            
            if (calendlyUrl && typeof calendlyUrl === 'string' && calendlyUrl.trim()) {
              setShowCalendlyPopup(true); // Show Calendly popup if link is available
            } else {
              setShowThankYouPopup(true); // Show thank you popup if no Calendly link
            }
          }
        } else if (stage === "Rejected Interview Request") {
          setRejectionCompleted(true);
        }
        setActive(false);
      } else {
        // Handle successful request but API returned error
        setError(response.data?.message || "Update failed with unknown error");
      }
    } catch (err) {
      console.error("Error updating interview request:", err);
      
      // Handle different types of errors
      if (err.response) {
        // The server responded with a status code outside the 2xx range
        console.error("Server response:", err.response.data);
        const errorMessage = err.response.data?.message || err.response.data?.error || "Server error";
        setError(`Server error: ${errorMessage}`);
      } else if (err.request) {
        // The request was made but no response was received
        console.error("No response received:", err.request);
        setError("No response from server. Please check your connection and try again.");
      } else {
        // Something happened in setting up the request
        setError(err.message || "Failed to update interview status");
      }
    } finally {
      if (stage === "Interviewing") {
        setIsAccepting(false);
      }
    }
  };

  // Reject Interview Request handler
  const handleReject = async () => {
    // Show feedback modal
    setShowFeedbackModal(true);
  };

  // Handle feedback submission - now matches the working modal pattern
  const handleFeedbackSubmit = async (feedbackData) => {
    setRejectionFeedback(feedbackData);
    setShowFeedbackModal(false);
    
    try {
      // Submit with the feedback data structure
      await handleSubmit("Rejected Interview Request", feedbackData);
    } catch (err) {
      setError(err.message || "Failed to submit feedback");
    }
  };

  const [showThankYouPopup, setShowThankYouPopup] = useState(false);
  const [showCalendlyPopup, setShowCalendlyPopup] = useState(false);

  // Helper to fetch application count (same logic as personalized_roles.js)
  const getApplicationCount = (r) => {
    const fields = [
      "# Apps this month",
      "Apps this month",
      "Applications this month",
      "# Applications this month",
      "App Count",
      "Application Count",
    ];
    for (const f of fields) {
      const val = r[f];
      if (val !== undefined && val !== null) {
        const num = parseInt(val);
        if (!isNaN(num)) return num;
      }
    }
    return null;
  };

  return (
    <>
      {/* All Modals - Rendered outside the card container using portals */}
      {showRationaleModal && createPortal(
        <RationaleModal
          isOpen={showRationaleModal}
          onClose={() => setShowRationaleModal(false)}
          onSubmit={handleRationaleSubmit}
          roleTitle={role["Role Title"]}
          Company={role["Name (from Startup)"]}
          role={role}
        />,
        document.body
      )}

      {showApplyConfirmPopup && createPortal(
        <ApplyConfirmModal
          isOpen={showApplyConfirmPopup}
          onClose={() => setShowApplyConfirmPopup(false)}
          onConfirm={executeApplyNow}
          roleTitle={role["Role Title"]}
          Company={role["Name (from Startup)"]}
        />,
        document.body
      )}

      {showOfferConfirmModal && createPortal(
        <OfferConfirmationModal
          isOpen={showOfferConfirmModal}
          onClose={() => setShowOfferConfirmModal(false)}
          onStatusSelect={handleOfferDecision}
          companyName={role["Name (from Startup)"]}
        />,
        document.body
      )}

      {showStatusCommentModal && createPortal(
        <StatusCommentModal
          isOpen={showStatusCommentModal}
          onClose={handleStatusCommentModalClose}
          onSubmit={handleStatusCommentSubmit}
          buttonInfo={selectedSubOption}
        />,
        document.body
      )}

      {showFeedbackModal && createPortal(
        <RejectFeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
          roleTitle={role["Role Title"]}
          onAccept={() => handleSubmit("Interviewing")}
        />,
        document.body
      )}

      {showAcceptInterviewModal && createPortal(
        <AcceptInterviewModal
          isOpen={showAcceptInterviewModal}
          onClose={() => {
            setShowAcceptInterviewModal(false);
            setInterviewAccepted(false); // Reset for next time
          }}
          onConfirm={() => {
            handleSubmit("Interviewing");
          }}
          roleTitle={role["Role Title"]}
          companyName={role["Name (from Startup)"]}
          candidateStream={candidateData?.stream}
          interviewAccepted={interviewAccepted}
          hasBookedFirstCall={candidateData?.hasBookedFirstCall || false}
          onBookCall={onBookCall}
        />,
        document.body
      )}

      {showCalendlyPopup && createPortal(
        <CalendlyPopup
          open={showCalendlyPopup}
          onClose={() => {
            setShowCalendlyPopup(false);
            if (onInterviewActionComplete) onInterviewActionComplete();
          }}
          company={role["Name (from Startup)"]}
          calendlyUrl={(() => {
            const urlField = role["HL Calendly Link (if applicable)"];
            if (!urlField) return null;
            if (typeof urlField === 'string') return urlField;
            if (Array.isArray(urlField) && urlField.length > 0) return urlField[0];
            return null;
          })()}
        />,
        document.body
      )}

      {showThankYouPopup && createPortal(
        <ThankYouPopup
          open={showThankYouPopup}
          onClose={() => {
            setShowThankYouPopup(false);
            if (onInterviewActionComplete) onInterviewActionComplete();
          }}
          company={role["Name (from Startup)"]}
        />,
        document.body
      )}

      <div className={cardWrapperClass}>
        {/* Media Section - Consistent height between video and image cards */}
        <div className="border-b border-gray-100 flex-shrink-0 bg-gradient-to-br from-gray-50 to-white">
          {hasSpotlights ? (
            <>
              {/* Videos Section with Tabs */}
              <div className="flex justify-between items-start px-4 pt-3">
                <div className="flex">
                  {hasOnlyCompanyAndRole ? (
                    <button
                      className="py-2 px-3 -mb-px border-b-2 border-[#02B491] text-[#02B491] font-medium text-sm"
                    >
                      Company and Role
                    </button>
                  ) : (
                    <>
                      <button
                        className={`py-2 px-3 -mb-px text-sm ${
                          activeTab === "company"
                            ? "border-b-2 border-[#02B491] text-[#02B491] font-medium"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                        onClick={() => setActiveTab("company")}
                      >
                        Company Spotlight
                      </button>
                      <button
                        className={`py-2 px-3 -mb-px text-sm ${
                          activeTab === "role"
                            ? "border-b-2 border-[#02B491] text-[#02B491] font-medium"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                        onClick={() => setActiveTab("role")}
                      >
                        Role Spotlight
                      </button>
                    </>
                  )}
                </div>
                {showSaveButton && (
                  <RoleSaveButton
                    isSaved={isSaved}
                    isUpdating={isUpdating}
                    onClick={handleSaveToggle}
                    isLoading={!shortlistedRolesLoaded}
                  />
                )}
              </div>
              <div className="p-4 pt-2">
                {hasOnlyCompanyAndRole ? (
                  <div>
                    {companyAndRoleSpotlights.map((spot, index) => (
                      <div key={index} className={`w-full ${compactView ? 'h-48' : 'h-80'} mb-3`}>
                        {renderSpotlightVideo(spot, index)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {activeTab === "company" && companySpotlights.length > 0 ? (
                      <div>
                        {companySpotlights.map((spot, index) => (
                          <div key={index} className={`w-full ${compactView ? 'h-48' : 'h-80'} mb-3`}>
                            {renderSpotlightVideo(spot, index)}
                          </div>
                        ))}
                      </div>
                    ) : activeTab === "company" ? (
                      <p className="text-gray-500 text-xs py-2">
                        No company spotlight available.
                      </p>
                    ) : null}
                    {activeTab === "role" && roleSpotlights.length > 0 ? (
                      <div>
                        {roleSpotlights.map((spot, index) => (
                          <div key={index} className={`w-full ${compactView ? 'h-48' : 'h-80'} mb-3`}>
                            {renderSpotlightVideo(spot, index)}
                          </div>
                        ))}
                      </div>
                    ) : activeTab === "role" ? (
                      <p className="text-gray-500 text-xs py-2">
                        No role spotlight available.
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Image Section with matching tab structure */}
              <div className="flex justify-between items-start px-4 pt-3">
                <div className="flex">
                  <div className="py-2 px-3 -mb-px text-sm text-gray-500">
                    Company Image
                  </div>
                </div>
                {showSaveButton && (
                  <RoleSaveButton
                    isSaved={isSaved}
                    isUpdating={isUpdating}
                    onClick={handleSaveToggle}
                    isLoading={!shortlistedRolesLoaded}
                  />
                )}
              </div>
              <div className="p-4 pt-2">
                <div className={`relative w-full ${compactView ? 'h-48' : 'h-80'} mb-3`}>
                  {/* Show image loading state */}
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-0">
                    <div className="text-gray-400 text-center p-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-8 w-8 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="mt-2 text-sm">Loading image...</p>
                    </div>
                  </div>
                  
                  <img
                    ref={imageRef}
                    src={coverImageUrl}
                    alt={role["Role Title"] || "Role"}
                    className="w-full h-full object-cover relative z-10 rounded-xl"
                    onLoad={() => console.log("Image loaded successfully:", coverImageUrl)}
                    onError={(e) => {
                      console.error("Image failed to load:", coverImageUrl);
                      console.error("For role:", role["Role Title"]);
                      console.error("Image element src at error time:", e.target.src);
                      
                      // If the image fails to load, set it to the fallback
                      if (imageRef.current && imageRef.current.src !== fallbackImageUrl) {
                        console.log("Using fallback image for:", role["Role Title"]);
                        imageRef.current.src = fallbackImageUrl;
                      }
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Role Information */}
        <div className={`p-4 flex flex-col flex-grow relative bg-gradient-to-br from-white to-gray-50 ${compactView ? 'pt-3 pb-3' : ''}`}>
          {saveError && (
            <div className="absolute top-0 right-0 left-0 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded-md text-sm z-10">
              {saveError}
            </div>
          )}
          
          {/* Interview Status Selection - Show at top for interviewing stage */}
          {renderInterviewStatusButtons()}
          
          {/* Content wrapper */}
          <div className={`${compactView ? 'space-y-2' : 'space-y-4'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-grow">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className={`font-bold ${compactView ? 'text-base leading-tight' : 'text-xl leading-tight'} text-gray-900 tracking-tight`}>
                    {role["Role Title"] || "(No role title)"}
                  </h2>
                  {role["Role Headcount"] === "Multiple headcount" && !compactView && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-gray-800 border border-gray-200 shadow-sm">
                      <Users className="w-3.5 h-3.5 mr-1 text-gray-600" />
                      Multiple openings
                    </span>
                  )}
                </div>
                <p className="text-gray-700 text-sm font-medium">
                  {role["Name (from Startup)"] || "Unnamed Company"}
                </p>
              </div>
              {!compactView && renderStageBadge()}
            </div>

            {/* Tags - Show fewer tags in compact view */}
            <div className="flex flex-wrap gap-2">
              {role["Location (from Startup)"] && (
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200 shadow-sm ${compactView ? 'text-[10px]' : ''}`}>
                  <MapPin className={`${compactView ? 'w-3 h-3 mr-0.5' : 'w-3.5 h-3.5 mr-1'}`} />
                  {role["Location (from Startup)"]}
                </span>
              )}
              {!compactView && role["Working style category"] && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200 shadow-sm">
                  <Timer className="w-3.5 h-3.5 mr-1" />
                  {role["Working style category"]}
                </span>
              )}
              {role["Compensation"] && (
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200 shadow-sm ${compactView ? 'text-[10px]' : ''}`}>
                  <Wallet className={`${compactView ? 'w-3 h-3 mr-0.5' : 'w-3.5 h-3.5 mr-1'}`} />
                  {role["Compensation"]}
                </span>
              )}
              {!compactView && role["Team Size (from Startup)"] && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200 shadow-sm">
                  <Users className="w-3.5 h-3.5 mr-1" />
                  {role["Team Size (from Startup)"]} people
                </span>
              )}
              {!compactView && role["Funding Stage (from Startup)"] && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200 shadow-sm">
                  <Building2 className="w-3.5 h-3.5 mr-1" />
                  {role["Funding Stage (from Startup)"]}
                </span>
              )}
            </div>

            {/* Company Description - Only show in non-compact view */}
            {!compactView && role["Startup Description (from Startup)"] && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-900">About the Company</h3>
                <div className="relative">
                  <div className={`relative ${!showFullDescription ? 'max-h-[2.5rem] overflow-hidden' : ''}`}>
                    <p
                      ref={descriptionRef}
                      className="text-sm text-gray-600"
                    >
                      {role["Startup Description (from Startup)"]}
                    </p>
                    {!showFullDescription && isDescriptionLong && (
                      <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-white pointer-events-none" />
                    )}
                  </div>
                  {isDescriptionLong && (
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="text-[#02B491] hover:text-[#018268] text-sm font-medium flex items-center gap-1 mt-1"
                    >
                      {showFullDescription ? (
                        <>
                          Show less
                          <ChevronUp className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          Show more
                          <ChevronDown className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Spacer to push button to bottom */}
          <div className="flex-grow" />

          {/* Learn More Link for Match Requests */}
          {role.stage === "Match requested" && !compactView && (
            <div className="mt-4 mb-3">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  console.log("Learn more clicked for role:", role["Role Title"]);
                  sessionStorage.setItem("lastVisitedPage", window.location.pathname);
                  // Pass from=interview-request and interviewId for match requests
                  window.open(`role?roleId=${role["URL ID"]}&from=interview-request&interviewId=${role.id}`, "_blank");
                  trackEngagementAction("cta_click", { cta: "learn_more", role_id: role.id, role_title: role["Role Title"] });
                }}
                className="text-[#02B491] hover:text-[#018268] text-sm font-medium flex items-center gap-1 transition-colors duration-200"
              >
                <ExternalLink className="w-4 h-4" />
                <span>View full role details</span>
              </button>
            </div>
          )}

          {/* Buttons Container */}
          <div className={`flex gap-2 ${compactView ? 'mt-2' : 'mt-4'}`}>
            {/* Match Request Accept/Decline Buttons */}
            {role.stage === "Match requested" && !compactView && (
              <>
                {active ? (
                  <>
                    <button
                      onClick={() => setShowAcceptInterviewModal(true)}
                      disabled={isAccepting}
                      className={`w-1/2 flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-[#02B491] text-white hover:bg-[#018268] hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold ${compactView ? 'text-sm' : ''} disabled:bg-gray-400 disabled:cursor-not-allowed`}
                    >
                      {isAccepting ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Processing...</span>
                        </>
                      ) : (
                        "Accept Interview Request"
                      )}
                    </button>
                    <button
                      onClick={handleReject}
                      className={`w-1/2 flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-red-500 text-white hover:bg-red-700 hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold ${compactView ? 'text-sm' : ''}`}
                    >
                      Decline
                    </button>
                  </>
                ) : accepted ? (
                  <div className="w-full text-center py-2 text-green-700 font-medium">
                    Thank you! You have accepted this interview request.
                    {/* Calendly embed logic can go here if available */}
                  </div>
                ) : rejectionCompleted ? (
                  <div className="w-full text-center py-2 text-gray-700 font-medium">
                    Thank you for your feedback. The request has been declined.
                  </div>
                ) : null}
                {error && (
                  <div className="w-full text-center py-2 text-red-600 text-sm">{error}</div>
                )}
              </>
            )}
            {/* Learn More Button - Remove Update Status Button since we now have inline status updates */}
            {role.stage !== "Match requested" && (
              <>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    console.log("Learn more clicked for role:", role["Role Title"]);
                    sessionStorage.setItem("lastVisitedPage", window.location.pathname);
                    window.open(`role?roleId=${role["URL ID"]}`, "_blank");
                    trackEngagementAction("cta_click", { cta: "learn_more", role_id: role.id, role_title: role["Role Title"] });
                  }}
                  className={`${showApplyNowButton ? 'w-1/2' : 'w-full'} flex items-center justify-center gap-2 py-3 px-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-lg hover:bg-[#02B491] hover:text-white hover:shadow-md transition-all duration-200 font-semibold ${compactView ? 'text-sm' : ''}`}
                >
                  <span className="font-medium">Learn more</span>
                  <ExternalLink className="w-4 h-4" />
                </button>
              </>
            )}
            {/* Apply Now Button */}
            {showApplyNowButton && !compactView && role.stage !== "Match requested" && !showUpdateStatusButton && (
              <button
                onClick={handleInternalApplyNow}
                disabled={!isDataFullyLoaded || isApplying || applyStatus.type === 'success' || hasAlreadyApplied || isCurrentlyInterviewing || isUnsuccessful}
                className={`w-1/2 flex items-center justify-center gap-2 py-3 px-4 rounded-lg transition-all duration-300 ease-in-out font-semibold ${getApplyButtonClasses()} ${compactView ? 'text-sm' : ''}`}
              >
                <AnimatePresence mode="wait">
                  {!isDataFullyLoaded ? (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="flex items-center gap-2"
                    >
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Loading...</span>
                    </motion.div>
                  ) : isApplying ? (
                    <motion.div
                      key="applying"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="flex items-center gap-2"
                    >
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Applying...</span>
                    </motion.div>
                  ) : isCurrentlyInterviewing ? (
                    <motion.div
                      key="interviewing"
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-2"
                    >
                      <CheckCircle2 className="w-5 h-5" /> 
                      <span>Interviewing</span>
                    </motion.div>
                  ) : isUnsuccessful ? (
                    <motion.div
                      key="unsuccessful"
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span>Unsuccessful</span>
                    </motion.div>
                  ) : (hasAlreadyApplied || applyStatus.type === 'success') ? (
                    <motion.div
                      key="applied"
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-2"
                    >
                      <CheckCircle2 className="w-5 h-5" /> 
                      <span>{hasAlreadyApplied ? 'Already Applied' : 'Applied!'}</span>
                    </motion.div>
                  ) : applyStatus.type === 'error' ? (
                     <motion.div
                      key="error"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-2"
                    >
                      <AlertTriangle className="w-5 h-5" /> 
                      <span>Error</span>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="applyNow"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      <span>Quick Apply</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            )}
          </div>
          {!hasAlreadyApplied && !isCurrentlyInterviewing && !isUnsuccessful && applyStatus.message && !isApplying && role.stage !== "Match requested" && (
            <p className={`text-xs mt-1.5 text-center ${applyStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {applyStatus.message}
            </p>
          )}

          {/* Debug overlay for match information */}
          {debug && (
            <div className="mt-2 bg-yellow-50 text-gray-700 text-[9px] p-2 rounded overflow-auto max-h-60">
              <div className="font-bold mb-1 text-blue-800">🔍 MATCH DEBUG INFO</div>
              <pre className="whitespace-pre-wrap leading-tight">
{`Score: ${role.matchScore ?? 'N/A'}/100
Apps: ${getApplicationCount(role) ?? 'N/A'}

ROLE DATA:
• Compensation: ${role["Compensation"] || 'N/A'}
• Working Style: ${role["Working style category"] || 'N/A'}
• Title: ${role["Role Title"] || 'N/A'}

CANDIDATE DATA:
• Min Salary: ${candidateData?.minimum_salary || 'N/A'}
• Ideal Salary: ${candidateData?.ideal_salary || 'N/A'}
• Working Styles: ${candidateData?.working_style ? JSON.stringify(candidateData.working_style) : 'N/A'}
• Experience 1: ${candidateData?.work_experience_1_role || 'N/A'}
• Experience 2: ${candidateData?.work_experience_2_role || 'N/A'}

MATCH RESULTS:
• Salary Match: ${role._debugInfo?.salaryMatch ? '✅' : '❌'}
• Working Style Match: ${role._debugInfo?.workingStyleMatch ? '✅' : '❌'}
• Experience Match: ${role._debugInfo?.experienceMatch ? '✅' : '❌'}
• Criteria Level: ${role._debugInfo?.criteriaLevel ?? 'N/A'}

              EXPERIENCE MATCHING:
              • Experience Matching: ❌ REMOVED (No longer used in criteria)
              • Embeddings: ❌ REMOVED (No longer used)
              • Base Score: ${role._debugInfo?.baseMatchScore ? `${role._debugInfo.baseMatchScore.toFixed(1)}` : 'N/A'}

SALARY PARSING DEBUG:
• Role Compensation Raw: "${role["Compensation"] || 'N/A'}"
• Candidate Min Raw: "${candidateData?.minimum_salary || 'N/A'}"
• Candidate Ideal Raw: "${candidateData?.ideal_salary || 'N/A'}"
• Role Parsed Range: ${role._debugInfo?.roleSalaryRange ? `${role._debugInfo.roleSalaryRange.min}k-${role._debugInfo.roleSalaryRange.max}k` : 'N/A'}
• Role Parsed Raw: ${role._debugInfo?.roleSalaryRange ? JSON.stringify(role._debugInfo.roleSalaryRange) : 'N/A'}
• Candidate Parsed Range: ${role._debugInfo?.candidateSalaryRange ? `${role._debugInfo.candidateSalaryRange.min}k-${role._debugInfo.candidateSalaryRange.max}k` : 'N/A'}
• Buffer Applied: ±5k
• Final Overlap Check: ${role._debugInfo?.salaryOverlapDetails || 'N/A'}`}
              </pre>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default RoleCard;